import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { QuestionSessionBodyContainerProps } from '../../QuestionSessions/QuestionSessionBody/config';
import { useReferentialQuestionContent } from '../../../hooks/content/useReferentialQuestionContent';
import { useStores } from '../../../hooks/useStores';

export interface SleepDetailsLabels {
  bedtime: string;
  timeToFallAsleep: string;
  wakeUpTime: string;
  nighttimeAwakenings: string;
  extraDetails: string;
  infoTitle: string;
  infoBody: string;
}

export interface SleepDetailsData extends Record<string, unknown> {
  labels: SleepDetailsLabels;
  bedtime: string;
  onChangeBedtime: (value: string) => void;
  bedtimeOptions: string[];
  timeToSleep: string;
  onChangeTimeToSleep: (value: string) => void;
  timeToSleepOptions: string[];
  wakeUp: string;
  onChangeWakeUp: (value: string) => void;
  wakeUpOptions: string[];
  awakenings: number;
  onChangeAwakenings: (value: number) => void;
  notes: string;
  onChangeNotes: (value: string) => void;
}

const timeOptions = (startHour: number, endHour: number) => {
  const options: string[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const h = ((hour % 24) + 24) % 24;
    const suffix = h >= 12 ? 'PM' : 'AM';
    const display = h % 12 === 0 ? 12 : h % 12;
    options.push(`${display}:00${suffix}`);
    options.push(`${display}:30${suffix}`);
  }
  return options;
};

const BEDTIME_OPTIONS = timeOptions(20, 27); // 8PM → 3:30AM
const WAKE_UP_OPTIONS = timeOptions(4, 12); // 4AM → 12:30PM
const TIME_TO_SLEEP_OPTIONS = ['5min', '10min', '15min', '20min', '30min', '45min', '1h', '1h30', '2h+'];

/**
 * Owns the sleep details form state and composes the readable diary entry
 * into the pending response on every change.
 */
const _ContainSleepDetails: FC<QuestionSessionBodyContainerProps> = ({
  question,
  children,
}) => {
  const { treeNav, language } = useStores();
  const { question: questionStore } = useStores();
  const loading = !questionStore.isReady(question._id);
  const { description, question: questionText } = useReferentialQuestionContent(question);
  const diaryLabels = language?.translate?.MOBILE?.DIARY;

  const labels: SleepDetailsLabels = {
    bedtime: diaryLabels?.BEDTIME || 'Bedtime',
    timeToFallAsleep: diaryLabels?.TIME_TO_FALL_ASLEEP || 'Time to fall asleep',
    wakeUpTime: diaryLabels?.WAKE_UP_TIME || 'Wake-up time',
    nighttimeAwakenings: diaryLabels?.NIGHTTIME_AWAKENINGS || 'Nighttime awakenings',
    extraDetails: diaryLabels?.EXTRA_DETAILS || 'Add any extra details (optional)',
    infoTitle: diaryLabels?.WHAT_ELSE_TITLE || 'What else should I add?',
    infoBody: diaryLabels?.WHAT_ELSE_BODY
      || 'This can be anything: what you ate or drank in the evening, whether you had alcohol or caffeine during the day, what you dreamed during the night...',
  };

  const [bedtime, setBedtime] = useState('11:30PM');
  const [timeToSleep, setTimeToSleep] = useState('20min');
  const [wakeUp, setWakeUp] = useState('7:30AM');
  const [awakenings, setAwakenings] = useState(0);
  const [notes, setNotes] = useState('');
  // the pickers show suggested defaults, but a night the user never described
  // must not be recorded: only compose the entry once they touch the form
  const [touched, setTouched] = useState(false);
  const touch = <T,>(setter: (value: T) => void) => (value: T) => {
    setTouched(true);
    setter(value);
  };

  useEffect(() => {
    if (!question?._id || !touched) {
      return;
    }
    const parts = [
      `${labels.bedtime}: ${bedtime}`,
      `${labels.timeToFallAsleep}: ${timeToSleep}`,
      `${labels.wakeUpTime}: ${wakeUp}`,
      `${labels.nighttimeAwakenings}: ${awakenings}`,
    ];
    if (notes.trim().length > 0) {
      parts.push(notes.trim());
    }
    treeNav.choices.updatePending({
      questionId: question._id,
      questionKey: question.key,
      // stamp the current context: a stale pending from an unfinished
      // session would otherwise leak its ids into this response
      surveyId: treeNav.content.survey?._id,
      pathId: treeNav.content.path?._id,
      sessionId: treeNav.content.session?._id,
      freeText: parts.join('\n'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?._id, touched, bedtime, timeToSleep, wakeUp, awakenings, notes]);

  const data: SleepDetailsData = {
    labels,
    bedtime,
    onChangeBedtime: touch(setBedtime),
    bedtimeOptions: BEDTIME_OPTIONS,
    timeToSleep,
    onChangeTimeToSleep: touch(setTimeToSleep),
    timeToSleepOptions: TIME_TO_SLEEP_OPTIONS,
    wakeUp,
    onChangeWakeUp: touch(setWakeUp),
    wakeUpOptions: WAKE_UP_OPTIONS,
    awakenings,
    onChangeAwakenings: touch(setAwakenings),
    notes,
    onChangeNotes: touch(setNotes),
  };

  return children({
    questionId: question._id,
    questionKey: question.key,
    questionType: question.type,
    questionText,
    questionDescription: description,
    loading,
    data,
  });
};

export const ContainSleepDetails = observer(_ContainSleepDetails);
